import { EXISTING_ACCOUNTS } from "../Data/existingAccounts";
import {
  getStaticLeaderboardRows,
  STATIC_OVERVIEW,
  waitForStaticFetch,
} from "../Data/staticOverview";
import { getPublicClientIp } from "./clientIp";
import { lookupAccountByContact, lookupKycStatus } from "./kycLookup";
import { isStaticDataMode } from "./staticMode";
import { normalizeEmail, normalizePhoneDigits } from "./signupValidation";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:7004/api/v1/competition";
const COMPETITION_ID = process.env.REACT_APP_COMPETITION_ID;

const getLandingUrl = () => {
  if (typeof window === "undefined" || !window.location?.origin) return "";
  return window.location.origin;
};

const okPayload = (result, message = "") => ({
  ok: true,
  status: 200,
  payload: {
    isSuccess: true,
    message,
    result,
  },
});

const parsePayload = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const request = async (path, options = {}) => {
  const landingUrl = getLandingUrl();
  const separator = path.includes("?") ? "&" : "?";
  const withLanding =
    landingUrl && !/[?&]landingUrl=/.test(path)
      ? `${path}${separator}landingUrl=${encodeURIComponent(landingUrl)}`
      : path;

  const response = await fetch(`${API_BASE}${withLanding}`, {
    cache: "no-store",
    ...options,
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-cache",
      ...(COMPETITION_ID ? { "X-Competition-Id": COMPETITION_ID } : {}),
      ...(options.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...(options.headers || {}),
    },
  });

  const payload = await parsePayload(response);

  // Prefer structured API errors over a generic throw so the UI can show the real reason.
  if (response.status >= 500 && !payload?.message) {
    throw new Error("toast.submitError");
  }

  return {
    ok: response.ok,
    status: response.status,
    payload,
  };
};

export const fetchCompetitionOverview = async () => {
  if (isStaticDataMode()) {
    await waitForStaticFetch();
    return okPayload(STATIC_OVERVIEW);
  }
  return request("/overview");
};

export const getTermsPdfUrl = (competitionId, lang = "en") => {
  const id = competitionId || COMPETITION_ID;
  const qs = new URLSearchParams();
  if (id) qs.set("competitionId", id);
  if (lang) qs.set("lang", lang);
  const landingUrl = getLandingUrl();
  if (landingUrl) qs.set("landingUrl", landingUrl);
  return `${API_BASE}/terms-pdf?${qs.toString()}`;
};

export const checkExistingContact = async ({
  email,
  phone,
  phoneCountryCode,
  fullName,
  countryOfResidence,
  nationality,
  dualNationality,
  profession,
  professionOther,
  sourceOfFunds,
  sourceOfFundsOther,
  usCitizen,
  hasReferralCode,
  referralCode,
} = {}) => {
  if (isStaticDataMode()) {
    await waitForStaticFetch(350);
    const emailMatch = EXISTING_ACCOUNTS.some(
      (item) => normalizeEmail(item.email) === normalizeEmail(email || "")
    );
    const phoneDigits = normalizePhoneDigits(phone || "");
    const phoneMatch =
      phoneDigits.length >= 7 &&
      EXISTING_ACCOUNTS.some((item) =>
        normalizePhoneDigits(item.phone).endsWith(phoneDigits.replace(/^0+/, ""))
      );

    return okPayload({
      exists: emailMatch || phoneMatch,
      message: emailMatch || phoneMatch ? "toast.alreadyExists" : "",
    });
  }

  const clientIp = await getPublicClientIp();
  return request("/signup/check-existing", {
    method: "POST",
    body: JSON.stringify({
      email,
      phone,
      phoneCountryCode,
      ...(fullName ? { fullName } : {}),
      ...(countryOfResidence ? { countryOfResidence } : {}),
      ...(nationality ? { nationality } : {}),
      ...(dualNationality ? { dualNationality } : {}),
      ...(profession ? { profession } : {}),
      ...(professionOther ? { professionOther } : {}),
      ...(sourceOfFunds ? { sourceOfFunds } : {}),
      ...(sourceOfFundsOther ? { sourceOfFundsOther } : {}),
      ...(usCitizen ? { usCitizen } : {}),
      ...(hasReferralCode != null ? { hasReferralCode } : {}),
      ...(referralCode ? { referralCode } : {}),
      ...(clientIp ? { clientIp } : {}),
    }),
  });
};

export const fetchLeaderboard = async () => {
  if (isStaticDataMode()) {
    await waitForStaticFetch();
    return okPayload({
      rows: getStaticLeaderboardRows(),
      initialBalance: 10000,
    });
  }
  return request("/leaderboard");
};

export const checkKyc = async (accountNumber) => {
  if (isStaticDataMode()) {
    await waitForStaticFetch(400);
    const result = lookupKycStatus(accountNumber);
    return okPayload(result);
  }

  const clientIp = await getPublicClientIp();
  return request("/signup/kyc", {
    method: "POST",
    body: JSON.stringify({
      accountNumber,
      ...(clientIp ? { clientIp } : {}),
    }),
  });
};

export const recoverAccount = async (body) => {
  if (isStaticDataMode()) {
    await waitForStaticFetch(400);
    const result = lookupAccountByContact(body);
    return okPayload(result);
  }

  const clientIp = await getPublicClientIp();
  return request("/signup/recover", {
    method: "POST",
    body: JSON.stringify({
      ...body,
      ...(clientIp ? { clientIp } : {}),
    }),
  });
};

export const saveCompetitionNickname = async (body) => {
  if (isStaticDataMode()) {
    await waitForStaticFetch(300);
    return okPayload({
      status: "saved",
      account: { nickname: body?.nickname || "" },
    });
  }

  const clientIp = await getPublicClientIp();
  const hasFiles = Boolean(
    body?.proofOfIdentity instanceof File
    || body?.proofOfIdentityBack instanceof File
    || body?.proofOfAddress instanceof File
  );
  const isProfileEdit = Boolean(body?.fullName) || hasFiles;

  if (isProfileEdit) {
    const data = new FormData();
    Object.entries(body || {}).forEach(([key, value]) => {
      if (value == null || value === false) return;
      if (value instanceof File) {
        data.append(key, value);
        return;
      }
      if (typeof value === "boolean") {
        data.append(key, String(value));
        return;
      }
      data.append(key, value);
    });
    if (clientIp) data.append("clientIp", clientIp);
    return request("/signup/nickname", {
      method: "POST",
      body: data,
    });
  }

  return request("/signup/nickname", {
    method: "POST",
    body: JSON.stringify({
      ...body,
      ...(clientIp ? { clientIp } : {}),
    }),
  });
};

export const checkNicknameAvailability = async ({
  nickname,
  accountNumber,
  email,
} = {}) => {
  if (isStaticDataMode()) {
    await waitForStaticFetch(200);
    const value = String(nickname || "").trim();
    const taken = /taken|admin|test/i.test(value);
    return okPayload({
      available: !taken,
      nickname: value,
      reason: taken ? "taken" : "available",
      suggestions: taken ? [`${value}1`, `${value}_2`, `${value}fx`].map((s) => s.slice(0, 16)) : [],
    });
  }

  const clientIp = await getPublicClientIp();
  return request("/signup/nickname/check", {
    method: "POST",
    body: JSON.stringify({
      nickname,
      ...(accountNumber ? { accountNumber } : {}),
      ...(email ? { email } : {}),
      ...(clientIp ? { clientIp } : {}),
    }),
  });
};

export const registerParticipant = async (form) => {
  if (isStaticDataMode()) {
    await waitForStaticFetch(800);
    const accountNumber = `9${String(Date.now()).slice(-6)}`;
    return okPayload({
      alreadyExists: false,
      account: {
        accountNumber,
        firstName: form?.firstName || "",
        middleName: form?.middleName || "",
        lastName: form?.lastName || "",
        email: form?.email || "",
        phone: form?.phone || "",
        countryOfResidence: form?.countryOfResidence || "",
        nationality: form?.nationality || "",
        dualNationality: form?.dualNationality || "",
        profession: form?.profession || "",
        sourceOfFunds: form?.sourceOfFunds || "",
        usCitizen: form?.usCitizen,
        referralCode: form?.referralCode || "",
        beneficialOwner: Boolean(form?.beneficialOwner),
        termsAccepted: Boolean(form?.termsAccepted),
        kycApproved: false,
      },
    });
  }

  const data = new FormData();
  const clientIp = await getPublicClientIp();

  Object.entries(form).forEach(([key, value]) => {
    if (value == null || value === false) return;
    if (value instanceof File) {
      data.append(key, value);
      return;
    }
    if (typeof value === "boolean") {
      data.append(key, String(value));
      return;
    }
    data.append(key, value);
  });

  if (clientIp) {
    data.append("clientIp", clientIp);
  }

  return request("/signup/register", {
    method: "POST",
    body: data,
  });
};
